import React from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { getEventTypeSvgIcon } from '../utilities'
import Button from './Button/Button';
import PriceDisplay from './PriceDisplay';
import Tag from './Tag';
import AttendeesIcon from './Icons/AttendeesIcon';
import MyCalendarIcon from './Icons/MyCalendarIcon';
import ArrowRightIcon from './Icons/ArrowRightIcon';
import LocationIcon from './Icons/LocationIcon';
import { theme } from '../theme';
import Typography from './Typography';
import { useTranslation } from 'react-i18next';
import { formatMonthDate, formatDayDate, formatEventDateRange } from '../utils/dateUtils';

const EventCardRow = ({ event }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t: tEventCard } = useTranslation('eventDetail');
  const { t: tCategories } = useTranslation('eventCategories');
  const { i18n } = useTranslation();

  // Formatear fechas con internacionalización
  const formattedMonthDate = formatMonthDate(event.startDate, i18n.language);
  const formattedDayDate = formatDayDate(event.startDate);

  // Formatear fecha/hora completa de inicio/fin usando formatEventDateRange
  const eventDateRange = formatEventDateRange(event.startDate, event.endDate, i18n.language);


  const generateSlug = (title) => {
    return title.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')
  }

  // Verifica si el usuario es el owner del evento
  const isOwner = user && event.owner.id === user.id;

  // Handler para navegar a la página de gestión
  const handleManageEventClick = (e) => {
    e.preventDefault();
    navigate(`/events/manage/${event.id}`);
  };

  return (
    <Link to={`/events/${event.id}/${generateSlug(event.title)}`}>
      <Event>
        <EventMedia>
          <EventTags>
            <Tag $variant='blur'>{tCategories(`type.${event.eventType}`)}</Tag>
            {Array.isArray(event.terrain) ? (
              event.terrain.map((tItem, index) => (
                <Tag $variant='blur' key={index}>{tCategories(`terrain.${tItem}`)}</Tag>
              ))
            ) : (
              <Tag $variant='blur'>{tCategories(`terrain.${event.terrain}`)}</Tag>
            )}

          </EventTags>
          {event.image ? (
            <EventCardImage src={event.image} alt={event.title} />
          ) : (
            <EventImagePlaceholder>
              {getEventTypeSvgIcon(event.eventType, theme.fill.defaultSubtle)}

              {/* <EventImagePlaceholderIcon src={getEventTypeIcon(event.eventType)} alt="empty state icon" className='placeholderImage-icon' /> */}
            </EventImagePlaceholder>
          )}
        </EventMedia>
        <EventContent>
          <MainInfo>
            <EventTitle $variant="title-4-bold" $variantTablet="title-5-bold" $variantMobile="body-1-bold">{event.title}</EventTitle>
            <CalendarWrapper>
              <CalendarDate>
                <CalendarMonth>
                  <Typography $variant="overline-semibold" color={theme.colors.defaultStrong} style={{ textTransform: 'uppercase' }}>
                    {formattedMonthDate}
                  </Typography>
                </CalendarMonth>
                <CalendarDay>
                  <Typography $variant="body-1-semibold" color={theme.colors.defaultMain}>{formattedDayDate}</Typography>
                </CalendarDay>
              </CalendarDate>

            </CalendarWrapper>
          </MainInfo>
          <SecondaryInfo>
            <SecondaryBlock>
              <AttendeesIcon height={16} width={16} fill={theme.colors.defaultStrong} />
              <Typography $variant='body-2-medium' color={theme.colors.defaultStrong}>
                {`${event.attendeesCount} ${event.attendeesCount === 1
                  ? tEventCard('eventAttendees.attendee')
                  : tEventCard('eventAttendees.attendees')}`}
              </Typography>
            </SecondaryBlock>
            <SecondaryBlock>
              <LocationIcon height={16} width={16} fill={theme.colors.defaultStrong} />
              <Typography $variant='body-2-medium' color={theme.colors.defaultStrong}>{event.shortLocation}</Typography>
            </SecondaryBlock>
            <SecondaryBlock>
              <MyCalendarIcon height={16} width={16} fill={theme.colors.defaultStrong} />
              <Typography $variant='body-2-medium' color={theme.colors.defaultStrong}>
                {eventDateRange.singleLine || `${eventDateRange.startLine} - ${eventDateRange.endLine}`}
              </Typography>
            </SecondaryBlock>
          </SecondaryInfo>
          <TertiaryInfo>
            <PriceDisplay tickets={event.tickets} showOptions={true} />
            {isOwner ? (
              <Button size="l" sizeTablet="l" sizeMobile="m" $variant="outline" onClick={handleManageEventClick}>
                {/* Gestionar Evento */}
                {tEventCard('eventDetailHeader.manageEventButton')}

                <ArrowRightIcon fill={theme.colors.defaultStrong} />
              </Button>
            ) : (
              <Button size="l" sizeTablet="l" sizeMobile="m" $variant="outline">
                {/* Ver evento */}
                {tEventCard('eventDetailHeader.viewEventButton')}

                <ArrowRightIcon fill={theme.colors.defaultStrong} />
              </Button>
            )}
          </TertiaryInfo>
        </EventContent>
      </Event>
    </Link>
  );
};

export default EventCardRow;

//Estilos del componente

const Event = styled.div`
  align-items: stretch;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.sm};
  color: black;
  display: flex;
  flex-direction: row;
  height: 100%;
  overflow: hidden;
  text-decoration: none;
  transition: all 0.5s ease-out allow-discrete;
  width: 100%;

  &:hover {
    border: 1px solid ${({ theme }) => theme.border.defaultStrong};
    box-shadow: 0 0 4px 0 rgb(0 0 0 / 8%), 0 4px 12px 0 rgb(0 0 0 / 12%);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  } 
`;

const EventImagePlaceholder = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultStrong};
  display: flex;
  height: 100%;
  justify-content: center;
  object-fit: cover;
  width: 100%;
`;

const EventImagePlaceholderIcon = styled.img`
  height: ${({ theme }) => theme.sizing.xxl};
  width: ${({ theme }) => theme.sizing.xxl};
`;

const EventMedia = styled.div`
  aspect-ratio: 4/3;
  display: flex;
  max-width: 330px;
  position: relative;
  width: 100%;

  &::before {
    background: linear-gradient(180deg, 
      rgb(0 0 0 / 50%) 0%, 
      rgb(0 0 0 / 49.5%) 2%, 
      rgb(0 0 0 / 48%) 4%, 
      rgb(0 0 0 / 46%) 6%, 
      rgb(0 0 0 / 42.5%) 8%, 
      rgb(0 0 0 / 38.5%) 10%, 
      rgb(0 0 0 / 33.5%) 12%, 
      rgb(0 0 0 / 28%) 14%, 
      rgb(0 0 0 / 22%) 16%, 
      rgb(0 0 0 / 16.5%) 18%, 
      rgb(0 0 0 / 11.5%) 20%, 
      rgb(0 0 0 / 7.5%) 22%, 
      rgb(0 0 0 / 4%) 24%, 
      rgb(0 0 0 / 2%) 26%, 
      rgb(0 0 0 / 0%) 28%, 
      rgb(0 0 0 / 0%) 100%, 
      rgb(0 0 0 / 0.5%) 100%);
    background-blend-mode: overlay;
    content: "";
    display: block;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;  
  }
`;

const EventCardImage = styled.img`
  aspect-ratio: 4 / 3;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const EventTags = styled.div`
  align-items: center;
  display:flex;
  flex-direction: center;
  gap: ${({ theme }) => theme.sizing.xs};
  left: ${({ theme }) => theme.sizing.sm};
  position: absolute;
  top: ${({ theme }) => theme.sizing.sm};
`;

const EventContent = styled.div`
  align-self: stretch;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    border-top-left-radius: ${({ theme }) => theme.sizing.sm};
    border-top-right-radius: ${({ theme }) => theme.sizing.sm};
    margin-top: -${({ theme }) => theme.sizing.sm};
  } 
`;

const EventTitle = styled(Typography)`
  align-self: flex-start;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  flex-grow: 1;
  -webkit-line-clamp: 2;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MainInfo = styled.div`
  align-items: flex-start;
  align-self: stretch;
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 0 var(--Spacing-none, 0) 0 0;
`;

const SecondaryInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const CalendarDate = styled.div`
  align-items: stretch;
  background: var(--bg-default-subtle, #FAFAFA);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  width: 48px;
`;

const CalendarMonth = styled.div`
  align-items: center;
  background-color: #EFEFEF;
  display: flex;
  height: 24px;
  justify-content: center;
`;

const CalendarDay = styled.div`
  align-items: center;
  display: flex;
  height: 28px;
  justify-content: center;
`;

const SecondaryBlock = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.defaultStrong};
  display: flex;
  flex-direction: row;
  font-feature-settings: 'ss01' on, 'ss05' on, 'ss06' on, 'ss07' on;  font-size: 13px;
  font-style: normal;;
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: 500;
  gap: ${({ theme }) => theme.sizing.xs};
  line-height: 100%;
  margin: unset;
`;

const TertiaryInfo = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const CalendarWrapper = styled.div`
  height: 1px;
`;
